<script lang="ts">

  import { Fzf } from 'fzf'
  import { VList } from 'virtua/svelte'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import Library from '@lucide/svelte/icons/library'
  import BookshelfSlot from '$lib/components/nuread/BookshelfSlot.svelte'
  import LibraryHeader from '$lib/components/nuread/LibraryHeader.svelte'
  import { getSeries } from "$lib/apis/library/api";
  import { servers } from '$lib/store'
  import { seriesDataEpoch } from '$lib/operations.svelte'
  import { filterNsfwSeries, prefs } from '$lib/preferences.svelte'

  let { route } = $props();
  const serverid = $derived(route.result.path.params.mediaid);
  const libid    = $derived(route.result.path.params.libraryid);

  let maxWidth = $state(0)
  let itemWidth = $state(279.5)
  let itemHeight = $state(416)
  let maxHeight = $state(0)
  /** Document Y where the VList starts (below navbar + LibraryHeader). */
  let listTop = $state(0)
  let listAnchor: HTMLDivElement | undefined = $state()
  let columns = $state(3)

  let libRes = $state({})
  let loading = $state(true)

  let _status = $state([])
  let _formats = $state([])
  let _genres = $state([])
  let _tags = $state([])
  let _years = $state([])
  let search = $state()

  let hLim = $derived(maxWidth<370 ? 170 : 280)
  const listHeight = $derived(Math.max(0, Math.floor(maxHeight - listTop)))
  const ghostCols = $derived(Math.max(columns, 3))
  const ghostRows = $derived(
    Math.max(2, Math.min(4, Math.ceil((listHeight || 400) / (itemHeight + 24))))
  )
  const ghostCount = $derived(ghostCols * ghostRows)

  $effect(() => {
    if (!maxWidth) return
    for (let i = 9; i > 1; i--) {
      const h = ((maxWidth - 10) * 1.492) / i
      if (h > hLim) {
        itemHeight = h
        itemWidth = (maxWidth - 10) / i
        columns = i
        break
      }
    }
  })

  $effect(() => {
    const el = listAnchor
    if (!el) return
    const sync = () => {
      // Document Y of the list start (stable if the page is scrolled).
      listTop = el.getBoundingClientRect().top + window.scrollY
    }
    sync()
    const ro = new ResizeObserver(sync)
    const header = el.previousElementSibling
    if (header) ro.observe(header)
    window.addEventListener('resize', sync)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', sync)
    }
  })

  $effect(() => {
    void seriesDataEpoch.value
    let cancelled = false
    loading = true
    getSeries($servers[serverid], libid, null)
      .then(r => { if (!cancelled) libRes = r })
      .finally(() => { if (!cancelled) loading = false })
    return () => { cancelled = true }
  })

  const series = $derived(libRes?.Series ?? []);
  let filteredSeries = $derived.by( () => {
    void prefs.displayNsfw
    const _series = filterNsfwSeries(series)
      .filter((s: any) =>
       _years.length==0 || _years.includes(s.Dates?.startDate?.year.toString())
      )
      .filter((s: any) => {
        if (_genres.length == 0) return true
        for (const genre of s?.genres ?? []) {
          if (_genres.includes(genre?.Name)) return true
        }
      })
      .filter((s: any) => {
        if (_tags.length == 0) return true
        for (const tag of s?.tags ?? []) {
          if (_tags.includes(tag?.Name)) return true
        }
      })
      .filter((s: any) =>
        _formats.length == 0 || _formats.includes(s.kind)
      )
      .filter((s: any) =>
        _status.includes("nsfw") ? s?.Status?.isAdult : true
      )
      .filter((s: any) =>
        _status.includes("new") ? s?.Progress?.Completed == 0 : true
      )
      if (!search) return _series
      return new Fzf(_series, {
          selector: (item) => item?.Infos?.title || item.localTitle
        }).find(search).map(({item}) => item)
  })
  let rowCount = $derived(Math.ceil(filteredSeries.length / columns))
  let rows = $derived(Array.from({ length: rowCount }, (_, i) => i))

  const genres = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series).map((s: any) => s?.genres
      ?.map((e: any) => e.Name)).flat().sort().filter(
        (el: any, i: number,arr: any[]) => (el && el != arr?.[i-1]) )
  })
  const tags = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series).map((s: any) => s?.tags
      ?.map((e: any) => e.Name)).flat().sort().filter(
        (el: any, i: number,arr: any[]) => (el && el != arr?.[i-1]) )
  })
  const formats = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series).map((s: any) => s.kind ).sort().filter(
      (e: any, i: number,arr: any[]) => (e && e != arr?.[i-1]) )
  })
  const years = $derived.by(() => {
    void prefs.displayNsfw
    return filterNsfwSeries(series).map((s: any) => s?.Dates?.startDate?.year.toString()).sort((a: any, b: any) => b-a ).filter(
      (e: any, i: number,arr: any[]) => (e && e != arr?.[i-1]) )
  })

function handleSelection(
    optionKind: 'format' | 'genre' | 'tag' | 'year' | 'status',
    value: string[]) {
    switch (optionKind) {
      case 'status':
        _status = value
        break;
      case 'year':
        _years = value
        break;
      case 'tag':
        _tags = value
        break;
      case 'genre':
        _genres = value
        break;
      case 'format':
        _formats = value
        break;
      default:
        break;
    }
}
</script>

<svelte:window bind:innerHeight={null, v => (maxHeight = v!)} />

<div class="w-full h-0" bind:clientWidth={null, v => (maxWidth = v!)}></div>

<LibraryHeader
  {genres}
  {tags}
  {formats}
  {years}
  {handleSelection}
  bind:search
  bind:selectedGenres={_genres}
  bind:selectedStatus={_status}
  bind:selectedYears={_years}
  bind:selectedFormats={_formats}
  bind:selectedTags={_tags}
/>

<!-- Sentinel: list height = viewport remaining below navbar + header -->
<div class="h-0 w-full" bind:this={listAnchor}></div>

{#if loading}
  <div
    class="series-loader flex flex-col items-center justify-center gap-5"
    style={`height: ${listHeight}px;`}
    role="status"
    aria-label="Loading series"
  >
    <div class="series-loader__orb">
      <span class="series-loader__ring series-loader__ring--outer"></span>
      <span class="series-loader__ring series-loader__ring--mid"></span>
      <span class="series-loader__ring series-loader__ring--inner"></span>
      <span class="series-loader__glow"></span>
      <span class="series-loader__icon">
        <BookOpen
          class="size-8 text-latte-green dark:text-macchiato-green"
          strokeWidth={1.75}
        />
      </span>
    </div>
    <p
      class="series-loader__label text-sm tracking-wide
        text-latte-text/70 dark:text-macchiato-text/70"
    >
      Loading series…
    </p>
  </div>
{:else if series.length == 0}
  <section
    class="lib-empty relative isolate overflow-hidden px-6 sm:px-10"
    style={`height: ${listHeight}px;`}
    aria-labelledby="lib-empty-title"
  >
    <div
      class="lib-empty__glow pointer-events-none absolute left-1/2 top-[42%] size-[min(36rem,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-green)_22%,transparent),transparent_68%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-green)_18%,transparent),transparent_68%)]"
      aria-hidden="true"
    ></div>
    <div
      class="lib-empty__glow-2 pointer-events-none absolute left-[max(0%,8%)] top-[12%] size-52 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-mauve)_14%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-mauve)_10%,transparent),transparent_70%)]"
      aria-hidden="true"
    ></div>
    <div
      class="lib-empty__glow-3 pointer-events-none absolute bottom-[10%] right-[max(0%,6%)] size-44 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-sky)_14%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-sky)_10%,transparent),transparent_70%)]"
      aria-hidden="true"
    ></div>
    <div
      class="lib-empty__haze pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,transparent_35%,color-mix(in_oklab,var(--color-latte-base)_55%,transparent)_100%)] dark:bg-[linear-gradient(to_bottom,transparent_35%,color-mix(in_oklab,var(--color-macchiato-base)_45%,transparent)_100%)]"
      aria-hidden="true"
    ></div>

    <div
      class="pointer-events-none absolute inset-0 z-[1] overflow-hidden py-4"
      aria-hidden="true"
    >
      <div
        class="mx-auto grid w-full max-w-[1600px] gap-x-6 gap-y-8 px-2 opacity-[0.42] sm:px-4"
        style={`grid-template-columns: repeat(${ghostCols}, minmax(0, 1fr));`}
      >
        {#each { length: ghostCount } as _, i (i)}
          {@const tilt = ((i % 5) - 2) * 1.1}
          {@const delay = (i % 7) * 0.09}
          <div
            class="lib-empty__ghost"
            style={`--ghost-delay: ${delay}s; --ghost-tilt: ${tilt}deg;`}
          >
            <div
              class="lib-empty__cover relative mx-auto overflow-hidden rounded-t-md border border-dashed border-latte-green/20 bg-latte-green/[0.04] dark:border-macchiato-green/20 dark:bg-macchiato-green/[0.06]"
              style={`width: calc(${itemWidth}px - 1.875rem); height: calc(${itemHeight}px - 3.125rem); max-width: 100%;`}
            >
              <span class="lib-empty__shimmer absolute inset-0"></span>
              <span
                class="absolute inset-x-[18%] top-[22%] h-px bg-latte-overlay0/40 dark:bg-macchiato-overlay0/35"
              ></span>
              <span
                class="absolute inset-x-[28%] top-[32%] h-px bg-latte-overlay0/30 dark:bg-macchiato-overlay0/25"
              ></span>
              <span
                class="absolute inset-x-[22%] top-[42%] h-px bg-latte-overlay0/20 dark:bg-macchiato-overlay0/18"
              ></span>
            </div>
            <div
              class="mx-auto mt-2 h-2.5 w-2/3 rounded-full bg-latte-surface0/70 dark:bg-macchiato-surface0/60"
            ></div>
            <div
              class="mx-auto mt-1.5 h-2 w-1/3 rounded-full bg-latte-surface0/45 dark:bg-macchiato-surface0/40"
            ></div>
          </div>
        {/each}
      </div>
    </div>

    <div
      class="lib-empty__stack relative z-[2] flex h-full flex-col items-center justify-center text-center"
    >
      <div class="lib-empty__mark relative select-none" aria-hidden="true">
        <div
          class="lib-empty__badge relative flex size-[clamp(5rem,16vw,7.5rem)] items-center justify-center rounded-[1.75rem] bg-latte-mantle/85 text-latte-green shadow-sm ring-1 ring-latte-surface1/60 backdrop-blur-sm sm:rounded-[2rem] dark:bg-macchiato-mantle/85 dark:text-macchiato-green dark:ring-macchiato-surface1/60"
        >
          <Library class="size-[42%] opacity-90" strokeWidth={1.75} />
          <span
            class="lib-empty__zero absolute right-3.5 top-3 block font-semibold leading-none tracking-tighter text-latte-text sm:right-4 sm:top-3.5 dark:text-macchiato-text"
            style="font-size: clamp(1.25rem, 4vw, 1.7rem);"
          >
            0
          </span>
        </div>
      </div>

      <h1
        id="lib-empty-title"
        class="lib-empty__title mt-6 text-2xl font-semibold tracking-tight text-latte-text sm:text-3xl dark:text-macchiato-text"
      >
        Nothing on this shelf
      </h1>
      <p
        class="lib-empty__copy mt-3 max-w-md text-sm leading-relaxed text-latte-text/70 sm:text-base dark:text-macchiato-text/70"
      >
        No series in this library yet — or it may not exist.
        <br /><br />
        Point a scan at your media folders and titles will fill in here.
      </p>
    </div>
  </section>
{:else}
  <VList
    bufferSize={itemHeight*1.6}
    data={rows}
    style={`height: ${listHeight}px;`}
    class="px-10 py-4 scrollbar-thin scrollbar-thumb-latte-surface1 scrollbar-track-latte-mantle dark:scrollbar-thumb-macchiato-surface1 dark:scrollbar-track-macchiato-crust"
    getKey={row => row}
  >
    {#snippet children(rowIndex)}
      <div
        class="grid gap-6 my-2"
        style={`grid-template-columns: repeat(${columns}, minmax(0, 1fr));`}
      >
        {#each filteredSeries.slice(rowIndex * columns, (rowIndex + 1) * columns) as _series (_series.id)}
          <BookshelfSlot
            data={{
              series: _series,
              dims: {
                height: itemHeight,
                width: itemWidth,
              },
              mediaId: serverid,
              libraryId: libid,
              cdnUrl: $servers[serverid].urls[0],
            }}
          />
        {/each}
      </div>
    {/snippet}
  </VList>
{/if}

<style>
  .lib-empty__glow {
    animation: lib-empty-breathe 7s ease-in-out infinite;
  }

  .lib-empty__glow-2 {
    animation: lib-empty-drift 11s ease-in-out infinite;
  }

  .lib-empty__glow-3 {
    animation: lib-empty-drift 13s ease-in-out infinite reverse;
  }

  .lib-empty__stack {
    animation: lib-empty-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .lib-empty__mark {
    animation: lib-empty-bob 5.5s ease-in-out infinite;
  }

  .lib-empty__zero {
    animation: lib-empty-wiggle 2.3s ease-in-out infinite;
    transform-origin: 50% 100%;
  }

  .lib-empty__title {
    animation: lib-empty-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.06s;
  }

  .lib-empty__copy {
    animation: lib-empty-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 0.12s;
  }

  .lib-empty__ghost {
    transform: rotate(var(--ghost-tilt));
    transform-origin: 50% 100%;
    animation: lib-empty-float 5.2s ease-in-out infinite;
    animation-delay: var(--ghost-delay);
  }

  .lib-empty__ghost:nth-child(even) {
    opacity: 0.72;
  }

  .lib-empty__shimmer {
    background: linear-gradient(
      115deg,
      transparent 35%,
      color-mix(in oklab, var(--color-latte-overlay0) 18%, transparent) 50%,
      transparent 65%
    );
    background-size: 220% 100%;
    animation: lib-empty-shimmer 4.8s ease-in-out infinite;
    animation-delay: var(--ghost-delay);
  }

  :global(.dark) .lib-empty__shimmer {
    background: linear-gradient(
      115deg,
      transparent 35%,
      color-mix(in oklab, var(--color-macchiato-overlay0) 22%, transparent) 50%,
      transparent 65%
    );
    background-size: 220% 100%;
  }

  @keyframes lib-empty-rise {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes lib-empty-bob {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes lib-empty-float {
    0%,
    100% {
      transform: rotate(var(--ghost-tilt, 0deg)) translateY(0);
    }
    50% {
      transform: rotate(var(--ghost-tilt, 0deg)) translateY(-6px);
    }
  }

  @keyframes lib-empty-breathe {
    0%,
    100% {
      opacity: 0.85;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.05);
    }
  }

  @keyframes lib-empty-drift {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(12px, -10px);
    }
  }

  @keyframes lib-empty-wiggle {
    0%,
    100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(-6deg);
    }
    75% {
      transform: rotate(6deg);
    }
  }

  @keyframes lib-empty-shimmer {
    0%,
    100% {
      background-position: 120% 0;
    }
    50% {
      background-position: -20% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .lib-empty__glow,
    .lib-empty__glow-2,
    .lib-empty__glow-3,
    .lib-empty__stack,
    .lib-empty__mark,
    .lib-empty__zero,
    .lib-empty__title,
    .lib-empty__copy,
    .lib-empty__ghost,
    .lib-empty__shimmer {
      animation: none;
    }
  }

  .series-loader__orb {
    position: relative;
    display: grid;
    place-items: center;
    width: 6.5rem;
    height: 6.5rem;
  }

  .series-loader__ring {
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    border: 2.5px solid transparent;
    pointer-events: none;
  }

  .series-loader__ring--outer {
    border-top-color: var(--color-latte-green);
    border-right-color: color-mix(in oklab, var(--color-latte-green) 35%, transparent);
    animation: series-spin 1.4s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
  }

  .series-loader__ring--mid {
    inset: 0.7rem;
    border-bottom-color: color-mix(in oklab, var(--color-latte-green) 55%, var(--color-latte-surface1));
    border-left-color: color-mix(in oklab, var(--color-latte-surface1) 50%, transparent);
    animation: series-spin 1.85s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite reverse;
  }

  .series-loader__ring--inner {
    inset: 1.4rem;
    border-top-color: color-mix(in oklab, var(--color-latte-green) 45%, transparent);
    border-left-color: transparent;
    animation: series-spin 1.1s linear infinite;
  }

  :global(.dark) .series-loader__ring--outer {
    border-top-color: var(--color-macchiato-green);
    border-right-color: color-mix(in oklab, var(--color-macchiato-green) 35%, transparent);
  }

  :global(.dark) .series-loader__ring--mid {
    border-bottom-color: color-mix(in oklab, var(--color-macchiato-green) 55%, var(--color-macchiato-surface1));
    border-left-color: color-mix(in oklab, var(--color-macchiato-surface1) 50%, transparent);
  }

  :global(.dark) .series-loader__ring--inner {
    border-top-color: color-mix(in oklab, var(--color-macchiato-green) 45%, transparent);
    border-left-color: transparent;
  }

  .series-loader__glow {
    position: absolute;
    inset: 1.7rem;
    border-radius: 9999px;
    background: radial-gradient(
      circle,
      color-mix(in oklab, var(--color-latte-green) 28%, transparent) 0%,
      transparent 70%
    );
    animation: series-pulse 1.8s ease-in-out infinite;
  }

  :global(.dark) .series-loader__glow {
    background: radial-gradient(
      circle,
      color-mix(in oklab, var(--color-macchiato-green) 30%, transparent) 0%,
      transparent 70%
    );
  }

  .series-loader__icon {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    animation: series-bob 1.8s ease-in-out infinite;
  }

  .series-loader__label {
    animation: series-fade 1.8s ease-in-out infinite;
  }

  @keyframes series-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes series-pulse {
    0%, 100% { opacity: 0.35; transform: scale(0.92); }
    50% { opacity: 0.85; transform: scale(1.05); }
  }

  @keyframes series-bob {
    0%, 100% { transform: translateY(0); opacity: 0.85; }
    50% { transform: translateY(-2px); opacity: 1; }
  }

  @keyframes series-fade {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
  }
</style>
